import React from 'react'
import { AppMessagePriority } from '@pushly/models/lib/enums/app-messages/app-message-priority'
import { getClassNames } from '../../../_utils/classnames'
import { RelativeDateDisplayMetric } from '@pushly/models/lib/enums/relative-date-display-metric'
import { enumAsSelectOptions } from '../../../_utils/antd'
import {
    AppMessageBehavior,
    MutableAppMessageBehavior,
} from '@pushly/models/lib/structs/app-messages/app-message-behavior'
import { RelativeDate } from '@pushly/models/lib/structs/relative-date'
import { convertSeconds, convertToSeconds } from '../../../_utils/time'
import { useAppMessageBuilderContext } from '../../../features/app-messages/context'
import { useState } from 'react'
import { AppMessageAnimationsConfigBuilder } from './app-message-animations-config-builder'
import { AppMessageSettingRow } from './app-message-setting-row'
import { DEFAULT_REDISPLAY_INTERVAL_DAYS } from '../constants'
import { Input, Radio, Select, Space } from 'antd'

export const AppMessageBehaviorBuilder: React.FC<React.PropsWithChildren<React.PropsWithChildren<unknown>>> = () => {
    const [context, dispatch] = useAppMessageBuilderContext()
    const { variants, selectedVariantIdx } = context
    const value = variants[selectedVariantIdx]
    const behavior = value.schedules![0].behavior
    const maxDisplayTime = behavior.maxDisplayTime
    const redisplay = behavior.redisplay

    const [plainFcap, setPlainFcap] = useState(
        redisplay.enabled ? convertSeconds(redisplay.fcap!.intervalSeconds, redisplay.fcap!.displayMetric) : null,
    )

    const handleBehaviorChange = (change: Partial<MutableAppMessageBehavior>) => {
        const newBehavior: MutableAppMessageBehavior = behavior.clone(change)

        // custom handle null setting
        if (change.maxDisplayTime === null) {
            newBehavior.maxDisplayTime = null
        }

        dispatch({ type: 'patch', entity: 'behavior', data: newBehavior })
    }

    return (
        <div className={getClassNames('app-message-behavior-builder')}>
            <AppMessageSettingRow
                label="Priority"
                htmlFor="amb-priority"
                info="App Message priority determines which message appears first when you send multiple in-app messages. High priority will display first."
            >
                <Radio.Group
                    id="amb-priority"
                    buttonStyle="solid"
                    optionType="button"
                    size="small"
                    value={value.priority}
                    options={enumAsSelectOptions(AppMessagePriority, 'number')}
                    disabled={context.disabled}
                    onChange={(ev) => {
                        dispatch({
                            type: 'patch',
                            entity: 'base',
                            data: value.clone({ priority: ev.target.value }),
                        })
                    }}
                />
            </AppMessageSettingRow>

            <AppMessageSettingRow
                label="Auto dismiss"
                info="This app message will be displayed in the application screen for the duration provided and will then be automatically removed from the view."
                toggle={{
                    checked: !!behavior.maxDisplayTime,
                    disabled: context.disabled,
                    onChange: (enabled) => {
                        let nextMaxDisplayTime: AppMessageBehavior['maxDisplayTime'] = null
                        if (enabled) {
                            nextMaxDisplayTime = {
                                intervalSeconds: 60,
                                displayMetric: RelativeDateDisplayMetric.SECONDS,
                            } as RelativeDate
                        }

                        handleBehaviorChange({ maxDisplayTime: nextMaxDisplayTime })
                    },
                }}
                offText="Stays on screen until dismissed"
            >
                <Space.Compact>
                    <Input
                        id="amb-auto-dismiss"
                        aria-label="Auto dismiss after"
                        disabled={context.disabled}
                        type="number"
                        min={1}
                        max={120}
                        value={
                            maxDisplayTime
                                ? convertSeconds(maxDisplayTime.intervalSeconds, maxDisplayTime.displayMetric)
                                : undefined
                        }
                        onChange={(ev) => {
                            const nextValue = parseInt(ev.target.value, 10)
                            // Clearing a number input hands back an empty string.
                            // Storing the NaN that produces fails the model's
                            // IsPositive at submit, on a value the readiness panel
                            // does not look at — so the last good value stands
                            // until a number replaces it.
                            if (!Number.isFinite(nextValue)) {
                                return
                            }

                            // Converted through the field's own metric rather than
                            // written as raw seconds. The two agree only while the
                            // metric happens to be seconds, which is the same
                            // mismatch that stored a one-day redisplay as one
                            // second.
                            handleBehaviorChange({
                                maxDisplayTime: {
                                    ...behavior.maxDisplayTime!,
                                    intervalSeconds: convertToSeconds(
                                        nextValue,
                                        behavior.maxDisplayTime!.displayMetric,
                                    ),
                                },
                            })
                        }}
                    />
                    <Space.Addon>
                        <span>Seconds</span>
                    </Space.Addon>
                </Space.Compact>
            </AppMessageSettingRow>

            <AppMessageSettingRow
                label="Allow redisplay"
                info="Determine how frequently an in app message is allowed to be displayed in a user application. Maximum 1 message per day (24 hours) as default."
                toggle={{
                    checked: redisplay.enabled,
                    disabled: context.disabled,
                    onChange: (enabled) => {
                        const nextFcap = enabled
                            ? {
                                  intervalSeconds: convertToSeconds(
                                      DEFAULT_REDISPLAY_INTERVAL_DAYS,
                                      RelativeDateDisplayMetric.DAYS,
                                  ),
                                  displayMetric: RelativeDateDisplayMetric.DAYS,
                              }
                            : null

                        setPlainFcap(DEFAULT_REDISPLAY_INTERVAL_DAYS)
                        handleBehaviorChange({ redisplay: redisplay.clone({ enabled, fcap: nextFcap }) })
                    },
                }}
                offText="Shown once per subscriber"
            >
                <Space.Compact>
                    <Input
                        id="amb-redisplay-value"
                        aria-label="Allow redisplay after"
                        disabled={context.disabled}
                        type="number"
                        min={1}
                        max={365}
                        value={redisplay.enabled && redisplay.fcap ? (plainFcap ?? 1) : undefined}
                        onChange={(ev) => {
                            const nextValue = parseInt(ev.target.value, 10)
                            if (!Number.isFinite(nextValue)) {
                                return
                            }

                            // fcap only exists when this field is enabled
                            const nextFcap = redisplay.fcap!.clone({
                                ...redisplay.fcap,
                                intervalSeconds: convertToSeconds(nextValue, redisplay.fcap!.displayMetric),
                            })

                            setPlainFcap(nextValue)
                            handleBehaviorChange({ redisplay: redisplay.clone({ fcap: nextFcap }) })
                        }}
                    />
                    <Select<RelativeDateDisplayMetric>
                        aria-label="Redisplay time metric"
                        value={redisplay.fcap?.displayMetric ?? RelativeDateDisplayMetric.DAYS}
                        options={enumAsSelectOptions(RelativeDateDisplayMetric).filter(
                            (opt) =>
                                opt.value !== RelativeDateDisplayMetric.SECONDS &&
                                opt.value !== RelativeDateDisplayMetric.MINUTES,
                        )}
                        disabled={context.disabled}
                        onChange={(metric) => {
                            handleBehaviorChange({
                                redisplay: redisplay.clone({
                                    fcap: {
                                        intervalSeconds: convertToSeconds(plainFcap!, metric),
                                        displayMetric: metric,
                                    },
                                }),
                            })
                        }}
                        style={{ width: 120 }}
                    />
                </Space.Compact>
            </AppMessageSettingRow>

            <AppMessageAnimationsConfigBuilder />
        </div>
    )
}
